import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';
import { CityKeySchema } from '../city-key/city-key';
import { VenuePhotoSchema } from '../venue-photo/venue-photo';

export const VENUE_NAME_MAX_LENGTH = 160;
export const VENUE_DESCRIPTION_MAX_LENGTH = 1000;
export const VENUE_ADDRESS_MAX_LENGTH = 500;
export const VENUE_CITY_LABEL_MAX_LENGTH = 120;
export const VENUE_CONTACT_NAME_MAX_LENGTH = 120;
export const VENUE_CONTACT_PHONE_MAX_LENGTH = 40;
export const VENUE_CONTACT_EMAIL_MAX_LENGTH = 254;

/**
 * Per-locale venue copy (N5, W7). Both locales required — U8's parity law
 * applied to content rather than to key trees.
 *
 * `city` is here and `cityKey` is on the base row: see `CityKeySchema`, which
 * carries the argument for why a venue spells its city twice.
 */
export const VenueTranslationSchema = z
  .object({
    name: z.string().trim().min(1).max(VENUE_NAME_MAX_LENGTH),
    description: z.string().trim().min(1).max(VENUE_DESCRIPTION_MAX_LENGTH),
    /** The full street address, as a member would read it out to a driver. */
    address: z.string().trim().min(1).max(VENUE_ADDRESS_MAX_LENGTH),
    city: z.string().trim().min(1).max(VENUE_CITY_LABEL_MAX_LENGTH),
  })
  .strict();

export type VenueTranslation = z.infer<typeof VenueTranslationSchema>;

export const VenueTranslationsSchema = makeTranslationsSchema(VenueTranslationSchema);

export type VenueTranslations = z.infer<typeof VenueTranslationsSchema>;

/**
 * A partner institution's meeting place (`03-meetings.md`).
 *
 * `name`/`description`/`address`/`city` are the **caller's locale**, resolved by
 * the reader; the admin edit form reads {@link VenueDetailSchema}, which carries
 * every locale (W7).
 *
 * **The contact fields are staff-facing and stay on the base row.** They are the
 * operational handle on a partner — who to telephone when a supervised meeting
 * goes wrong — not content a member reads, so they are not translated and they
 * are not part of any member payload this contract will grow.
 *
 * **`isActive` is publication, not deletion.** An inactive venue is invisible to
 * members and still fully editable by staff; withdrawing one is a `DELETE`, which
 * is a soft delete (N2). The admin copy has to keep the two verbs apart, because
 * "deactivate" reads like "remove" to anyone who has not been told otherwise.
 */
export const VenueSchema = z
  .object({
    id: UuidSchema,
    cityKey: CityKeySchema,
    isActive: z.boolean(),
    contactName: z.string(),
    contactPhone: z.string(),
    contactEmail: z.string().nullable(),
    name: z.string(),
    description: z.string(),
    address: z.string(),
    city: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type Venue = z.infer<typeof VenueSchema>;

/**
 * What the admin edit form reads: every locale, not just the caller's (W7).
 *
 * **`photos` is on the detail rather than on {@link VenueSchema}** because a list
 * row does not draw them and would pay a join per venue to carry them. They are
 * ordered by the venue's own `sortOrder`, and each entry is an id to exchange at
 * `GET /media/:id/url` — see {@link VenuePhotoSchema} for why not a url.
 *
 * They are also **not** part of `VenueCreateSchema`: a photograph is uploaded and
 * bound in its own act, against a venue that already exists, so it never travels
 * through the form's submit.
 */
export const VenueDetailSchema = VenueSchema.extend({
  translations: VenueTranslationsSchema,
  photos: z.array(VenuePhotoSchema),
  /**
   * How many photographs this venue may hold — `meetings.maxVenuePhotos`,
   * resolved.
   *
   * On the payload rather than read from the settings api by the gallery,
   * because the control has to say **"3 of 8"** and disable "add" *with the
   * number on screen*: an add button that is simply absent at the ceiling is a
   * control that has stopped explaining itself (U0). Resolved server-side so the
   * client never re-derives a policy value, and travelling beside the photographs
   * it bounds so the two can never be read from different moments.
   */
  maxPhotos: z.number().int().positive(),
}).strict();

export type VenueDetail = z.infer<typeof VenueDetailSchema>;
